import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { rememberGame } from '../services/recentGames';
import { 
  Game, 
  Player, 
  GameStatus, 
  Stack, 
  StackEntry, 
  EntryType,
  PlayerQueue,
  getNextExpectedEntry,
  canSubmitToStack,
  getExpectedEntryType,
  isStackComplete 
} from '../types/models';
import JoinGame from './JoinGame';
import GameLobby from './GameLobby';
import InitialPrompt from './InitialPrompt';
import DrawPrompt from './DrawPrompt';
import GuessDrawing from './GuessDrawing';
import { Button, Center, Stack as MantineStack, Text, Title, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import GameCompletion from './GameCompletion';
import StackView from './StackView';
import { QueueLobby } from './ui/QueueLobby';
import { useGameChannel } from '../services/websocket';
import NotFound from './NotFound';
import LobbyAvatarEditor from './LobbyAvatarEditor';
import LobbyAvatarHistory from './LobbyAvatarHistory';

// Structural check (rather than instanceof ApiError) so it works on errors
// crossing module boundaries, e.g. mocked modules in tests
const isNotFoundError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: number }).status === 404;

const DEFAULT_POLL_INTERVAL_MS = 5000;
// Each poll produces a new game object and re-renders the draw subtree, which
// can janky a stroke in progress, so polling slows down while the player is on
// a drawing turn; WebSocket events still trigger refetches
const DRAWING_POLL_INTERVAL_MS = 30000;
// Game start broadcasts one stack_create per player plus a game_update; a
// trailing debounce collapses the burst into a single refetch
const WS_INVALIDATE_DEBOUNCE_MS = 250;

const hasActiveDrawingTurn = (game: Game | null | undefined): boolean => {
  if (!game || game.status !== GameStatus.IN_PROGRESS) return false;
  const playerId = game.currentPlayerId?.toString();
  if (!playerId) return false;
  const activeStack = game.stacks.find(
    stack => stack.activePlayerId === playerId && stack.status === 'active'
  );
  if (!activeStack) return false;
  return getExpectedEntryType(activeStack, playerId) === EntryType.DRAWING;
};

const GameContainer: React.FC = () => {
  console.log('--- GameContainer Rendering --- ');
  const { gameKey } = useParams<{ gameKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [selectedStack, setSelectedStack] = useState<Stack | null>(null);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [playerIdReady, setPlayerIdReady] = useState(false);
  const [showQueueLobby, setShowQueueLobby] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const isAvatarHistoryRoute = location.pathname.endsWith('/avatar/history');
  const isAvatarRoute = location.pathname.endsWith('/avatar') && !isAvatarHistoryRoute;
  const loadingMarkup = (
    <div style={{ minHeight: '100vh', backgroundColor: '#e1f2ef', paddingTop: '100px' }}>
      <MantineStack align="center" gap="md">
        <Title order={1} c="#0da388" style={{ fontWeight: 300 }}>
          drawwww.xyz
        </Title>
        <Loader type="bars" size="lg" color="#0da388" />
      </MantineStack>
    </div>
  );

  useEffect(() => {
    if (!gameKey) return;

    const params = new URLSearchParams(location.search);
    const playerId = params.get('player_id');

    if (playerId) {
      localStorage.setItem(`game_${gameKey}_player_id`, playerId);
      params.delete('player_id');
      const search = params.toString();
      navigate(
        {
          pathname: `/games/${gameKey}`,
          search: search ? `?${search}` : ''
        },
        { replace: true }
      );
    }

    setPlayerIdReady(true);
  }, [gameKey, location.search, navigate]);

  // Fetch game data with React Query
  const { data: gameResponse, error: queryError, isLoading, refetch } = useQuery({
    queryKey: ['game', gameKey],
    queryFn: async () => {
      if (!gameKey) throw new Error('No game key provided');
      const response = await apiClient.getGameByKey(gameKey);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    enabled: !!gameKey && playerIdReady,
    refetchInterval: (query) =>
      hasActiveDrawingTurn(query.state.data) && !showQueueLobby
        ? DRAWING_POLL_INTERVAL_MS
        : DEFAULT_POLL_INTERVAL_MS,
    // A 404 is definitive; transient failures (flaky mobile networks) get extra retries
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 3,
  });

  // Derive state from query response
  const game = gameResponse || null;
  const currentPlayer = game?.currentPlayerId ? 
    game.players.find(p => p.id === game.currentPlayerId?.toString()) || null :
    null;
  const playerQueues = game?.playerQueues || [];

  useEffect(() => {
    if (game?.status !== GameStatus.IN_PROGRESS && showQueueLobby) {
      setShowQueueLobby(false);
    }
  }, [game?.status, showQueueLobby]);

  useEffect(() => {
    if (game?.key) {
      rememberGame(game.key, game.title ?? null, game.status ?? null);
    }
  }, [game?.key, game?.title, game?.status]);

  useEffect(() => {
    if (!game || (!isAvatarRoute && !isAvatarHistoryRoute)) return;
    if (!game.isCurrentPlayerJoined || game.status !== GameStatus.LOBBY) {
      navigate(`/games/${game.key}`, { replace: true });
    }
  }, [game, isAvatarRoute, isAvatarHistoryRoute, navigate]);

  // Show join form if not joined
  useEffect(() => {
    if (game && !game.isCurrentPlayerJoined) {
      setShowJoinForm(true);
    } else {
      setShowJoinForm(false);
    }
  }, [game]);

  useEffect(() => {
    if (currentPlayer) {
      setIsJoining(false);
    }
  }, [currentPlayer]);

  // Helper function to check if player should see completed view
  const shouldShowCompletedView = (player: Player, game: Game): boolean => {
    // Only show completion view when game is actually complete
    if (game.status === GameStatus.COMPLETED) {
      return true;
    }
    
    // During game start transition, don't show completed view
    if (!game.stacks || game.stacks.length === 0) {
      return false;
    }

    // Check if all stacks have their expected entries and are complete
    return game.stacks.every(stack => 
      stack.expectedEntries && 
      stack.expectedEntries.length > 0 && 
      isStackComplete(stack)
    );
  };

  // Helper function to get pending stacks for a player
  const getPendingStacksForPlayer = (player: Player, game: Game): Stack[] => {
    return game.stacks.filter(stack => {
      // Get this player's entries in this stack
      const playerEntries = stack.entries.filter(entry => entry.playerId === player.id);
      // If player has made fewer entries than total turns, and they're the current holder,
      // this stack is pending for them
      return playerEntries.length < game.totalTurnsPerPlayer && 
             stack.currentHolderId === player.id;
    });
  };

  const invalidateTimerRef = useRef<number | null>(null);

  const invalidateGameQueryDebounced = useCallback(() => {
    if (invalidateTimerRef.current !== null) {
      window.clearTimeout(invalidateTimerRef.current);
    }
    invalidateTimerRef.current = window.setTimeout(() => {
      invalidateTimerRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
    }, WS_INVALIDATE_DEBOUNCE_MS);
  }, [gameKey, queryClient]);

  useEffect(() => {
    return () => {
      if (invalidateTimerRef.current !== null) {
        window.clearTimeout(invalidateTimerRef.current);
      }
    };
  }, []);

  // WebSocket subscription setup
  const callbacks = React.useMemo(() => ({
    onConnected: ({ reconnected }: { reconnected: boolean }) => {
      // Events broadcast while disconnected (e.g. backgrounded mobile tab) were
      // lost, so refetch to catch up immediately instead of waiting for the poll
      if (reconnected) {
        console.log('GameContainer: WebSocket reconnected, invalidating query');
        queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
      }
    },
    onRejected: () => {
      notifications.show({
        title: 'Live updates unavailable',
        message: 'Real-time connection was rejected. The game will refresh every few seconds instead.',
        color: 'orange',
      });
    },
    onGameUpdate: (updatedGame: Game) => {
      console.log('GameContainer: Received game update, invalidating query');
      // Just invalidate the query - React Query will handle the update
      invalidateGameQueryDebounced();
    },
    onPlayerUpdate: (updatedPlayer: Player) => {
      console.log('GameContainer: Received player update, invalidating query');
      invalidateGameQueryDebounced();
    },
    onPlayerJoin: (joinedPlayer: Player) => {
      console.log('GameContainer: Player joined, invalidating query');
      invalidateGameQueryDebounced();
    },
    onPlayerRemoved: (removedPlayer: Player) => {
      console.log('GameContainer: Player removed:', removedPlayer);
      // If the current player was removed, clear local storage
      if (currentPlayer && removedPlayer.id === currentPlayer.id) {
        localStorage.removeItem(`game_${gameKey}_player_id`);
        setShowJoinForm(true);
      }
      invalidateGameQueryDebounced();
    },
    onStackUpdate: (updatedStack: Stack) => {
      console.log('GameContainer: Stack updated, invalidating query');
      invalidateGameQueryDebounced();
    },
    onStackCreate: (newStack: Stack) => {
      console.log('GameContainer: Stack created, invalidating query');
      invalidateGameQueryDebounced();
    },
    onQueueUpdate: (updatedQueue: PlayerQueue) => {
      console.log('GameContainer: Queue updated, invalidating query');
      invalidateGameQueryDebounced();
    }
  }), [gameKey, currentPlayer, queryClient, invalidateGameQueryDebounced]); // Dependencies for stable callback reference

  useGameChannel(gameKey || '', callbacks);

  // Stable references keep the memoized drawing surface from re-rendering on
  // every poll-driven game object change
  const handleEntrySubmitted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
  }, [gameKey, queryClient]);

  const handleReturnToQueueLobby = useCallback(() => {
    setShowQueueLobby(true);
  }, []);

  // Handler for when player is successfully created by JoinGame component
  const handlePlayerCreated = (player: Player) => {
    console.log('Player created:', player);
    setIsJoining(true);
    setShowJoinForm(false); // Hide the join form
    setShowQueueLobby(false);
    // Invalidate query to fetch fresh data
    queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
  };

  // Loading and Error States
  if (!playerIdReady || isLoading || (!game && !queryError)) {
    return loadingMarkup;
  }

  if (queryError && !game) {
    if (isNotFoundError(queryError)) {
      return <NotFound />;
    }
    // Non-404 errors (network blips, server hiccups) are recoverable: offer a retry
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#e1f2ef', paddingTop: '100px' }}>
        <MantineStack align="center" gap="md">
          <Title order={1} c="#0da388" style={{ fontWeight: 300 }}>
            drawwww.xyz
          </Title>
          <Text c="dimmed">{queryError.message || 'Could not load the game.'}</Text>
          <Button color="#0da388" onClick={() => refetch()}>
            Try again
          </Button>
        </MantineStack>
      </div>
    );
  }

  if (!game) {
    // Should ideally be covered by error state, but as a fallback
    return <NotFound message="Game data could not be loaded." />;
  }

  // Show Join Game form if needed
  if (showJoinForm) {
    return <JoinGame game={game} onPlayerCreated={handlePlayerCreated} />;
  }
  
  // Render game based on status if player is joined
  if (!currentPlayer) {
    if (isJoining) {
      return loadingMarkup;
    }
    // This case shouldn't happen if showJoinForm is false, but handle defensively
    console.error('Inconsistent state: Player should be joined but not found.');
    return <NotFound message="An error occurred loading player data." />;
  }

  if (isAvatarHistoryRoute) {
    if (game.status !== GameStatus.LOBBY) {
      return null;
    }
    if (!currentPlayer.discordUserId) {
      navigate(`/games/${game.key}/avatar`, { replace: true });
      return null;
    }
    return (
      <LobbyAvatarHistory
        game={game}
        currentPlayer={currentPlayer}
        onCancel={() => navigate(`/games/${game.key}/avatar`, { replace: true })}
        onApplied={() => {
          queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
          navigate(`/games/${game.key}`, { replace: true });
        }}
      />
    );
  }

  if (isAvatarRoute) {
    if (game.status !== GameStatus.LOBBY) {
      return null;
    }
    return (
      <LobbyAvatarEditor
        game={game}
        currentPlayer={currentPlayer}
        onCancel={() => navigate(`/games/${game.key}`, { replace: true })}
        onHistory={() => navigate(`/games/${game.key}/avatar/history`, { replace: true })}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
          navigate(`/games/${game.key}`, { replace: true });
        }}
      />
    );
  }

  // If we're viewing a specific stack, show the stack view
  if (selectedStack) {
    return (
      <StackView
        game={game}
        stack={selectedStack}
        onBack={() => setSelectedStack(null)}
      />
    );
  }

  // Show the appropriate game state component
  switch (game.status) {
    case GameStatus.LOBBY:
      console.log('--- Rendering GameLobby with players:', game.players);
      return (
        <GameLobby 
          game={game} 
          currentPlayer={currentPlayer}
          onGameStart={() => {
            // Invalidate query to get fresh game state
            queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
          }}
        />
      );

    case GameStatus.IN_PROGRESS:
      // Check if player has completed all their turns first
      if (shouldShowCompletedView(currentPlayer, game)) {
        return (
          <GameCompletion
            game={game}
            currentPlayer={currentPlayer}
            stacks={game.stacks}
          />
        );
      }

      // Find the stack the player originated
      const playerStack = game.stacks.find(
        stack => stack.originPlayerId === currentPlayer.id
      );
      const currentActiveStack = game.stacks.find(
        stack => stack.activePlayerId === currentPlayer.id && stack.status === 'active'
      );
      
      console.log('Player stack:', playerStack);
      console.log('Expected entries:', playerStack?.expectedEntries);

      if (showQueueLobby) {
        return (
          <QueueLobby
            game={game}
            currentPlayer={currentPlayer}
            playerQueues={playerQueues}
            activeStack={currentActiveStack}
            onResumeActiveStack={() => setShowQueueLobby(false)}
            onStackPickup={async (stack) => {
              try {
                const { error } = await apiClient.pickupStack(game.key, stack.id);
                if (error) {
                  console.error('Failed to pick up stack:', error);
                  notifications.show({
                    title: 'Failed to pick up work',
                    message: error || 'Unable to pick up this stack. Please try again.',
                    color: 'red',
                  });
                  return;
                }
                await queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
                setShowQueueLobby(false);
                console.log('Stack picked up:', stack);
              } catch (err: any) {
                console.error('Error picking up stack:', err);
                const errorMessage = err?.error || err?.message;
                if (errorMessage) {
                  notifications.show({
                    title: 'Failed to pick up work',
                    message: errorMessage,
                    color: 'red',
                  });
                } else {
                  notifications.show({
                    title: 'Connection error',
                    message: 'Unable to connect to the server. Please check your internet connection.',
                    color: 'red',
                  });
                }
                await queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
              }
            }}
          />
        );
      }

      // Check if the player needs to create their initial prompt
      const needsInitialPrompt = 
        playerStack && 
        playerStack.entries.length === 0 &&
        playerStack.expectedEntries?.length > 0 &&
        playerStack.expectedEntries[0].expectedPlayerId === currentPlayer.id.toString() &&
        playerStack.expectedEntries[0].entryType === EntryType.TEXT &&
        !playerStack.expectedEntries[0].completed;

      if (needsInitialPrompt) {
        console.log('Player needs to create initial prompt:', playerStack);
        return (
          <InitialPrompt
            game={game}
            currentPlayer={currentPlayer}
            stack={playerStack}
            onPromptSubmitted={() => { 
              setSelectedStack(null);
              // Just invalidate query - let backend state update
              queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
              console.log('Prompt submitted, waiting for WebSocket update...');
            }}
            onReturnToLobby={() => setShowQueueLobby(true)}
          />
        );
      }

      // Check if user has an active stack from backend data
      // If user is actively working on a stack, show the work interface
      if (currentActiveStack) {
        const expectedType = getExpectedEntryType(currentActiveStack, currentPlayer.id);
        const nextEntry = getNextExpectedEntry(currentActiveStack);
        
        console.log('=== STACK DEBUG ===');
        console.log('Current Player:', currentPlayer);
        console.log('Active Stack:', currentActiveStack);
        console.log('Expected Entries:', currentActiveStack.expectedEntries);
        console.log('Next Entry:', nextEntry);
        console.log('Expected Type for Player:', expectedType);
        console.log('Stack Entries:', currentActiveStack.entries);
        console.log('==================');

        if (expectedType === EntryType.TEXT) {
          // When expecting text, we need to show the most recent DRAWING for them to guess
          const sortedEntries = [...currentActiveStack.entries].sort((a, b) => a.orderIndex - b.orderIndex);
          const lastDrawing = sortedEntries.filter(e => e.entryType === 'drawing').pop();
          
          if (!lastDrawing) {
            console.error('No drawing found to guess!', currentActiveStack);
            return <div>Error: No drawing found to guess</div>;
          }
          
          return (
            <GuessDrawing
              key={`${currentActiveStack.id}-${(nextEntry?.orderIndex ?? 0) + 1}`}
              game={game}
              currentPlayer={currentPlayer}
              stack={currentActiveStack}
              drawingUrl={lastDrawing.content}
              entryNumber={(nextEntry?.orderIndex ?? 0) + 1}
              turnsRemaining={game.players.length - currentActiveStack.entries.filter(e => e.playerId === currentPlayer.id).length}
              onGuessSubmitted={handleEntrySubmitted}
            />
          );
        } else { // Expected type is DRAWING
          // When expecting drawing, we need to show the most recent TEXT as the prompt
          const sortedEntries = [...currentActiveStack.entries].sort((a, b) => a.orderIndex - b.orderIndex);
          const lastText = sortedEntries.filter(e => e.entryType === 'text').pop();
          
          if (!lastText) {
            console.error('No text found to draw!', currentActiveStack);
            return <div>Error: No text prompt found</div>;
          }
          
          return (
            <DrawPrompt
              key={`${currentActiveStack.id}-${(nextEntry?.orderIndex ?? 0) + 1}`}
              game={game}
              currentPlayer={currentPlayer}
              stack={currentActiveStack}
              prompt={lastText.content}
              entryNumber={(nextEntry?.orderIndex ?? 0) + 1}
              turnsRemaining={game.players.length - currentActiveStack.entries.filter(e => e.playerId === currentPlayer.id).length}
              onDrawingSubmitted={handleEntrySubmitted}
              onReturnToLobby={handleReturnToQueueLobby}
            />
          );
        }
      } else {
        // Show queue lobby for picking up work
        return (
          <QueueLobby
            game={game}
            currentPlayer={currentPlayer}
            playerQueues={playerQueues}
            activeStack={currentActiveStack}
            onResumeActiveStack={() => setShowQueueLobby(false)}
            onStackPickup={async (stack) => {
              try {
                // Call API to pick up the stack
                const { error } = await apiClient.pickupStack(game.key, stack.id);
                if (error) {
                  console.error('Failed to pick up stack:', error);
                  notifications.show({
                    title: 'Failed to pick up work',
                    message: error || 'Unable to pick up this stack. Please try again.',
                    color: 'red',
                  });
                  return;
                }
                // Invalidate query to get fresh state with active stack
                await queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
                setShowQueueLobby(false);
                console.log('Stack picked up:', stack);
              } catch (err: any) {
                console.error('Error picking up stack:', err);
                // API errors have { error: string } shape from interceptor
                const errorMessage = err?.error || err?.message;
                if (errorMessage) {
                  notifications.show({
                    title: 'Failed to pick up work',
                    message: errorMessage,
                    color: 'red',
                  });
                } else {
                  notifications.show({
                    title: 'Connection error',
                    message: 'Unable to connect to the server. Please check your internet connection.',
                    color: 'red',
                  });
                }
                // Refresh game state to get current queue status
                await queryClient.invalidateQueries({ queryKey: ['game', gameKey] });
              }
            }}
          />
        );
      }

    case GameStatus.COMPLETED:
      return (
        <GameCompletion
          game={game}
          currentPlayer={currentPlayer}
          stacks={game.stacks}
        />
      );

    default:
      return <div className="game-container__error">Invalid game state</div>;
  }
};

export default GameContainer; 
